import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../hooks/useData";
import { PageHeader, DataTable, StatusBadge, KpiCard } from "../components/shared";
import { num, fmtDate } from "../lib/api";
import { Workflow, Boxes, CheckCircle2, Layers } from "lucide-react";

export default function ProductionStage() {
  const { data, loading } = useData("/production-orders");
  const navigate = useNavigate();
  const [design, setDesign] = useState("");

  const started = (o) => (o.stages || []).some((s) => s.name === "Cutting" && (s.qty_in || 0) > 0);
  const inProd = useMemo(() => (data || []).filter(started), [data]);
  const designs = useMemo(() => [...new Set(inProd.map((o) => o.design_number).filter(Boolean))], [inProd]);
  const rows = design ? inProd.filter((o) => o.design_number === design) : inProd;

  const activeCount = inProd.filter((o) => o.status !== "Finished").length;
  const doneCount = inProd.filter((o) => o.status === "Finished").length;

  const balance = (o) => Math.max(0, (o.total_qty || 0) - (o.finished_qty || 0));

  const columns = [
    { key: "design_number", header: "Design No", render: (r) => <button onClick={(e) => { e.stopPropagation(); setDesign(r.design_number); }} className="font-semibold text-blue-800 hover:underline">{r.design_number}</button> },
    { key: "code", header: "Prod Order", mono: true },
    { key: "colour", header: "Colour" },
    { key: "item_type", header: "Item" },
    { key: "total_qty", header: "Order Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "current_process", header: "Current Stage", render: (r) => <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{r.current_process}</span> },
    { key: "finished_qty", header: "Finished", align: "right", render: (r) => num(r.finished_qty) },
    { key: "balance", header: "Balance", align: "right", accessor: (r) => balance(r), render: (r) => num(balance(r)) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageHeader title="Production Stage" subtitle="Live tracking of orders in production — by design number" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Designs in Production" value={num(designs.length)} icon={Layers} accent="blue" />
        <KpiCard label="Active Orders" value={num(activeCount)} icon={Workflow} accent="sky" />
        <KpiCard label="Completed" value={num(doneCount)} icon={CheckCircle2} accent="emerald" />
        <KpiCard label="Total in Production" value={num(inProd.length)} icon={Boxes} accent="violet" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setDesign("")} data-testid="design-chip-all"
          className={`px-3 py-1 rounded-full text-xs border ${!design ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-300"}`}>All Designs</button>
        {designs.map((d) => (
          <button key={d} onClick={() => setDesign(d)} data-testid={`design-chip-${d}`}
            className={`px-3 py-1 rounded-full text-xs border ${design === d ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-600 border-slate-300"}`}>{d}</button>
        ))}
      </div>

      {!loading && <DataTable testid="production-stage-table" columns={columns} rows={rows} exportName="production-stage.csv" onRowClick={(r) => navigate(`/production-orders/${r.id}`)} />}
    </div>
  );
}
